import { createContext, PropsWithChildren } from 'react';
import { Box, Button, Divider, Flex, Grid, GridItem } from '@chakra-ui/react';

import { useEntityState } from '@app/hooks';
import { noop } from '@app/utils';

import { FormFooterProps, FormProps, FormValidation, ItemSpan } from './types';
import { useForm } from './hooks/use-form';
import { FormItem, FormOutletItem } from './from-item';

type FormContextValue<T = any> = {
  instance: Partial<T>;
  errors: Record<string, string>;
  onChange(field: keyof T): (value: any) => void;
  addValidation(field: keyof T, title: string, validation: FormValidation): void;
  addRequired(field: keyof T, title: string): void;
}

export const FormContext = createContext<FormContextValue>({
  instance: {},
  errors: {},
  onChange: () => noop,
  addValidation: noop,
  addRequired: noop,
});

export function Form<T>(props: FormProps<T>) {
  if (!props.onChange) {
    return <FormWithInternalState {...props} />; 
  }
  return (<FormWithOuterState {...props} />);
}

function FormWithOuterState<T>(props: FormProps<T>) {
  const { onSubmitForm, onAlternativeSubmitForm, contextValue } = useForm<T>(props);

  return (<FormContext.Provider value={contextValue}>
  <FormWrapper>
    {props.children}
    <FormFooter 
      submitText={props.submitText}  
      alternativeText={props.alternativeText}
      alternativeColorScheme={props.alternativeColorScheme}
      cancelText={props.cancelText}
      onCancel={props.onCancel}
      onAlternativeSubmit={onAlternativeSubmitForm}
      alternativeElement={props.alternativeElement}
      onSubmit={props.onSubmit ? onSubmitForm : undefined}
      isLoading={contextValue.isLoading}
      disabled={props.disabled}
    />
  </FormWrapper>
</FormContext.Provider>);
}


function FormWithInternalState<T>(props: FormProps<T>){
  const [instance, onChange] = useEntityState<T>(props.instance || {});
  const { onSubmitForm, onAlternativeSubmitForm, contextValue } = useForm<T>({...props, instance, onChange});

  return (<FormContext.Provider value={contextValue}>
    <FormWrapper>
      {props.children}
      <FormFooter 
        submitText={props.submitText}  
        alternativeText={props.alternativeText}
        cancelText={props.cancelText}
        onCancel={props.onCancel}
        onAlternativeSubmit={onAlternativeSubmitForm}
        alternativeElement={props.alternativeElement}
        onSubmit={props.onSubmit ? onSubmitForm : undefined}
        isLoading={contextValue.isLoading}
        disabled={props.disabled}
      />
    </FormWrapper>
  </FormContext.Provider>);
}

const FormWrapper = ({ children }: PropsWithChildren) => {
  return <Grid templateColumns={{ base: '1fr', md: 'repeat(12, 1fr)' }} gap={5}>
    {children}
  </Grid>;
};

function FormFooter(props: FormFooterProps) {
  const {
    submitText = 'Сохранить',
    cancelText = 'Отмена',
    alternativeText,
    alternativeColorScheme,
    onSubmit,
    onAlternativeSubmit,
    alternativeElement,
    onCancel,
    isLoading,
    disabled,
  } = props;

  return <GridItem colSpan={{ base: 1, md: 12 }}>
    <Flex justify='space-between'>
      {onSubmit && <Button isDisabled={disabled} onClick={onSubmit} isLoading={isLoading} colorScheme='brand'>{submitText}</Button>}
      {onAlternativeSubmit && <Button isLoading={isLoading} colorScheme={alternativeColorScheme} onClick={onAlternativeSubmit}>{alternativeText || ''}</Button>}
      {alternativeElement}
      <Button onClick={onCancel}>{cancelText}</Button>
    </Flex>
  </GridItem>;
}

function FormSpacer(props: { span?: ItemSpan } & PropsWithChildren) {
  return <GridItem colSpan={{ ...props.span || { base: 1, md: 6 } }}>{props.children}</GridItem>;
}

Form.Item = FormItem;
Form.OutletItem = FormOutletItem;
Form.Divider = () => <GridItem colSpan={{ base: 1, md: 12 }}><Box><Divider  /></Box></GridItem>;
Form.Spacer = FormSpacer;